import React, { useMemo, useState } from "react";
import { AlertTriangle, Info, Sparkles, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { SaveChip } from "@/components/ui/SaveChip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

// Phase 1.7 — Expanded telecom service models with descriptions.
// Stored as text[] in assessment_infrastructure_scope.telecom_service_model,
// so adding new labels requires no schema migration. Future reports can render
// the array directly.
const SERVICE_MODELS: { key: string; label: string; description: string }[] = [
  { key: "MNO", label: "MNO", description: "Mobile Network Operator — owns and operates mobile radio, transport, and core network infrastructure." },
  { key: "MVNO", label: "MVNO", description: "Mobile Virtual Network Operator — provides mobile services using another operator's radio infrastructure." },
  { key: "ISP", label: "ISP", description: "Internet Service Provider — provides internet connectivity and network services." },
  { key: "FiberCo", label: "FiberCo", description: "Fiber Network Operator — owns and operates fiber infrastructure and transport assets." },
  { key: "TowerCo", label: "TowerCo", description: "Tower Infrastructure Provider — owns and manages telecommunications towers and cell-site facilities." },
  { key: "Wholesale Carrier", label: "Wholesale Carrier", description: "Carrier's Carrier — provides backbone, interconnect, transit, and carrier services." },
  { key: "Neutral Host", label: "Neutral Host", description: "Provides shared infrastructure used by multiple operators." },
  { key: "Private Network Operator", label: "Private Network Operator", description: "Operates dedicated enterprise or industrial telecom networks." },
  { key: "OTT Provider", label: "OTT Provider", description: "Provides communications services over third-party networks." },
  { key: "Hyperscaler / TechCo", label: "Hyperscaler / TechCo", description: "Operates large-scale cloud, edge, and digital infrastructure." },
  { key: "IoT / M2M Operator", label: "IoT / M2M Operator", description: "Provides connectivity services for machine-to-machine or IoT deployments." },
  { key: "Other", label: "Other", description: "Reviewer-defined telecom operating model." },
];

// Phase 1.5 — Expanded component catalog. New entries were added below the
// original list to support the service-model auto-suggestion mapping without
// disrupting existing scope selections already persisted.
const COMPONENTS = [
  "RAN (Radio Access Network)",
  "Core Network (5G SBA / EPC / IMS)",
  "Transport / Backhaul",
  "Signaling (SS7 / Diameter / GTP)",
  "Roaming / IPX",
  "NFV / MEC",
  "OSS / BSS",
  "Subscriber Data Stores (HSS / UDM)",
  "IoT / M2M Gateway",
  "Data Centre / Co-location",
  "Edge Sites / POPs",
  "Customer Premises Equipment",
  // Phase 1.5 additions
  "Cloud / Edge Infrastructure",
  "Customer Edge / CPE",
  "Customer Edge / SIM Provisioning",
  "Fiber / Fixed Access Network",
  "POP / Exchange / Meet-Me-Room",
  "Cable Landing / Subsea",
  "Tower / Cell Site Infrastructure",
  "Power / Generator / Fuel Systems",
  "Environmental Monitoring",
  "Physical Site Security",
  "Field Vendor / Maintenance Access",
  "DDoS / Edge Protection",
  "Shared DAS / Small Cell Infrastructure",
];

// Phase 1.5 — Default component suggestions per Telecom Service Model.
// Adding a service model adds these to Components Present (merged, no
// duplicates). Removing a service model never removes components — reviewer
// must remove them manually to avoid accidental scope loss.
const SERVICE_MODEL_COMPONENT_MAP: Record<string, string[]> = {
  "MNO": [
    "RAN (Radio Access Network)",
    "Core Network (5G SBA / EPC / IMS)",
    "Transport / Backhaul",
    "Signaling (SS7 / Diameter / GTP)",
    "Roaming / IPX",
    "OSS / BSS",
    "Subscriber Data Stores (HSS / UDM)",
    "Data Centre / Co-location",
    "Cloud / Edge Infrastructure",
  ],
  "MVNO": [
    "OSS / BSS",
    "Subscriber Data Stores (HSS / UDM)",
    "Roaming / IPX",
    "Cloud / Edge Infrastructure",
    "Customer Edge / SIM Provisioning",
  ],
  "ISP": [
    "Transport / Backhaul",
    "Fiber / Fixed Access Network",
    "Data Centre / Co-location",
    "Cloud / Edge Infrastructure",
    "OSS / BSS",
    "Customer Edge / CPE",
  ],
  "FiberCo": [
    "Fiber / Fixed Access Network",
    "Transport / Backhaul",
    "Data Centre / Co-location",
    "POP / Exchange / Meet-Me-Room",
    "Cable Landing / Subsea",
  ],
  "TowerCo": [
    "Tower / Cell Site Infrastructure",
    "Power / Generator / Fuel Systems",
    "Environmental Monitoring",
    "Physical Site Security",
    "Field Vendor / Maintenance Access",
  ],
  "Wholesale Carrier": [
    "Transport / Backhaul",
    "Signaling (SS7 / Diameter / GTP)",
    "Roaming / IPX",
    "POP / Exchange / Meet-Me-Room",
    "Data Centre / Co-location",
    "DDoS / Edge Protection",
    "Cloud / Edge Infrastructure",
  ],
  "Neutral Host": [
    "RAN (Radio Access Network)",
    "Tower / Cell Site Infrastructure",
    "Transport / Backhaul",
    "Shared DAS / Small Cell Infrastructure",
    "Physical Site Security",
    "Field Vendor / Maintenance Access",
  ],
  "Private Network Operator": [
    "RAN (Radio Access Network)",
    "Core Network (5G SBA / EPC / IMS)",
    "Transport / Backhaul",
    "Cloud / Edge Infrastructure",
    "OSS / BSS",
    "Customer Edge / CPE",
  ],
  "OTT Provider": [
    "Cloud / Edge Infrastructure",
    "Data Centre / Co-location",
    "Customer Edge / CPE",
    "OSS / BSS",
    "DDoS / Edge Protection",
  ],
  "Hyperscaler / TechCo": [
    "Cloud / Edge Infrastructure",
    "Data Centre / Co-location",
    "POP / Exchange / Meet-Me-Room",
    "Transport / Backhaul",
    "DDoS / Edge Protection",
  ],
  "IoT / M2M Operator": [
    "IoT / M2M Gateway",
    "Customer Edge / CPE",
    "Cloud / Edge Infrastructure",
    "Subscriber Data Stores (HSS / UDM)",
    "OSS / BSS",
  ],
  // "Other" intentionally omitted — no auto-suggestion.
};

interface Props {
  disabled?: boolean;
  saveState: any;
  serviceModel: string[];
  componentsPresent: string[];
  componentsAssessed: string[];
  notes: string;
  onServiceModelChange: (v: string[]) => void;
  onComponentsPresentChange: (v: string[]) => void;
  onComponentsAssessedChange: (v: string[]) => void;
  onNotesChange: (v: string) => void;
}

function toggle(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function unionPreserveOrder(base: string[], additions: string[]): string[] {
  const seen = new Set(base);
  const out = [...base];
  for (const a of additions) {
    if (!seen.has(a)) {
      out.push(a);
      seen.add(a);
    }
  }
  return out;
}

export const InfrastructureScopeCard: React.FC<Props> = ({
  disabled,
  saveState,
  serviceModel,
  componentsPresent,
  componentsAssessed,
  notes,
  onServiceModelChange,
  onComponentsPresentChange,
  onComponentsAssessedChange,
  onNotesChange,
}) => {
  // Phase 1.5 — Transient note shown after auto-suggestion fires.
  const [suggestionNote, setSuggestionNote] = useState<{
    model: string;
    added: string[];
  } | null>(null);

  // ── Phase 1.6 — Scope validation ──
  const validation = useMemo(() => {
    const presentSet = new Set(componentsPresent);
    const assessedSet = new Set(componentsAssessed);
    const presentNotAssessed = componentsPresent.filter((c) => !assessedSet.has(c));
    const assessedNotPresent = componentsAssessed.filter((c) => !presentSet.has(c));
    const coverage =
      componentsPresent.length === 0
        ? null
        : Math.round((componentsAssessed.length / componentsPresent.length) * 100);
    return {
      presentNotAssessed,
      assessedNotPresent,
      noneAssessed:
        componentsPresent.length > 0 && componentsAssessed.length === 0,
      missingNotes:
        componentsPresent.length > 0 &&
        presentNotAssessed.length > 0 &&
        notes.trim().length === 0,
      coverage,
    };
  }, [componentsPresent, componentsAssessed, notes]);

  // Phase 1.5 — Handle service-model toggle with additive auto-suggestion.
  const handleServiceModelToggle = (modelKey: string) => {
    const wasSelected = serviceModel.includes(modelKey);
    const nextModels = wasSelected
      ? serviceModel.filter((m) => m !== modelKey)
      : [...serviceModel, modelKey];

    onServiceModelChange(nextModels);

    // Only auto-suggest on add (never on remove) and never for "Other".
    if (!wasSelected) {
      const suggested = SERVICE_MODEL_COMPONENT_MAP[modelKey] ?? [];
      if (suggested.length > 0) {
        const merged = unionPreserveOrder(componentsPresent, suggested);
        const added = suggested.filter((c) => !componentsPresent.includes(c));
        if (added.length > 0) {
          onComponentsPresentChange(merged);
          setSuggestionNote({ model: modelKey, added });
        }
      }
    }
  };

  return (
    <section className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Assessment Scope
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Select the telecom service model(s) and the infrastructure components in scope.
            Applicable C1–C8 modules are derived from this selection and drive the
            Infrastructure Security score.
          </p>
        </div>
        <SaveChip state={saveState} />
      </div>

      {/* Telecom Service Model — multi-select with hover descriptions */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Telecom Service Model
        </label>
        <div className="flex flex-wrap gap-1.5">
          {SERVICE_MODELS.map((m) => {
            const active = serviceModel.includes(m.key);
            return (
              <HoverCard key={m.key} openDelay={200}>
                <HoverCardTrigger asChild>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleServiceModelToggle(m.key)}
                    className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {m.label}
                  </button>
                </HoverCardTrigger>
                <HoverCardContent className="w-72 text-xs" side="top">
                  <div className="font-semibold text-foreground mb-1">{m.label}</div>
                  <p className="text-muted-foreground">{m.description}</p>
                </HoverCardContent>
              </HoverCard>
            );
          })}
        </div>
      </div>

      {/* Phase 1.5 — Auto-suggestion confirmation banner */}
      {suggestionNote && (
        <ValidationBanner severity="info" icon={<Sparkles className="h-4 w-4" />}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="font-medium">
                Suggested components were added based on{" "}
                <span className="font-semibold">{suggestionNote.model}</span>.
                Reviewer may modify scope.
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {suggestionNote.added.map((c) => (
                  <Badge key={c} variant="outline" className="text-[10px]">
                    {c}
                  </Badge>
                ))}
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  disabled={disabled}
                  onClick={() => {
                    onComponentsAssessedChange(
                      unionPreserveOrder(componentsAssessed, suggestionNote.added),
                    );
                  }}
                  className="h-7 text-xs"
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                  Mark suggested components as Assessed
                </Button>
              </div>
            </div>
            <button
              type="button"
              className="text-[11px] underline text-muted-foreground hover:text-foreground"
              onClick={() => setSuggestionNote(null)}
            >
              Dismiss
            </button>
          </div>
        </ValidationBanner>
      )}

      {/* Quick action: mark all Present components as Assessed */}
      {validation.presentNotAssessed.length > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="text-xs text-foreground">
            <span className="font-medium">{validation.presentNotAssessed.length}</span> component
            {validation.presentNotAssessed.length === 1 ? " is" : "s are"} marked Present but not yet Assessed. Telecom-specific controls surface only after components are marked Assessed.
          </div>
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={disabled}
            onClick={() =>
              onComponentsAssessedChange(
                unionPreserveOrder(componentsAssessed, componentsPresent),
              )
            }
            className="h-7 text-xs whitespace-nowrap"
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Mark all Present as Assessed
          </Button>
        </div>
      )}




      {/* Components Present / Assessed grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ComponentList
          title="Infrastructure Components Present"
          help="Components that exist in the operator's environment."
          values={componentsPresent}
          options={COMPONENTS}
          disabled={disabled}
          onChange={(v) => {
            // Cascade: if a component is removed from Present, also remove it
            // from Assessed to prevent Rule 3 violations.
            const presentSet = new Set(v);
            const filteredAssessed = componentsAssessed.filter((c) =>
              presentSet.has(c),
            );
            onComponentsPresentChange(v);
            if (filteredAssessed.length !== componentsAssessed.length) {
              onComponentsAssessedChange(filteredAssessed);
            }
          }}
        />
        <ComponentList
          title="Infrastructure Components Assessed"
          help="Subset of present components covered by this assessment."
          values={componentsAssessed}
          options={COMPONENTS}
          disabled={disabled}
          gatedBy={componentsPresent}
          onChange={onComponentsAssessedChange}
          highlightMissing={componentsPresent}
        />
      </div>

      {/* Phase 2.7C — "Infrastructure Coverage" informational banner removed
          from the reviewer UI. validation.coverage is still computed and may
          be consumed by internal diagnostics or future admin tooling. */}


      {/* Validation banners */}
      {validation.assessedNotPresent.length > 0 && (
        <ValidationBanner severity="error" icon={<AlertTriangle className="h-4 w-4" />}>
          <div className="font-medium">Assessed components must also be marked as Present.</div>
          <ul className="mt-1 list-disc pl-5">
            {validation.assessedNotPresent.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </ValidationBanner>
      )}
      {validation.noneAssessed && (
        <ValidationBanner severity="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          No infrastructure components have been marked as assessed.
          Infrastructure Security conclusions may not be representative of the
          operator's overall environment.
        </ValidationBanner>
      )}
      {!validation.noneAssessed && validation.presentNotAssessed.length > 0 && (
        <ValidationBanner severity="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          {validation.presentNotAssessed.length === 1 ? (
            <span>
              <span className="font-medium">{validation.presentNotAssessed[0]}</span> is
              marked as Present but Not Assessed. Infrastructure Security
              conclusions and scoring do not include this environment.
            </span>
          ) : (
            <div>
              <div className="font-medium">
                The following components are present but were not assessed:
              </div>
              <ul className="mt-1 list-disc pl-5">
                {validation.presentNotAssessed.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <div className="mt-1">
                Infrastructure Security conclusions do not include these
                environments.
              </div>
            </div>
          )}
        </ValidationBanner>
      )}
      {validation.missingNotes && (
        <ValidationBanner severity="info" icon={<Info className="h-4 w-4" />}>
          Consider documenting why certain infrastructure components were not
          assessed.
        </ValidationBanner>
      )}

      {/* Scope notes */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Scope Notes
        </label>
        <Textarea
          placeholder="Sites, regions, exclusions, methodology limitations…"
          className="text-xs min-h-[72px]"
          defaultValue={notes}
          disabled={disabled}
          onBlur={(e) => {
            const v = e.target.value;
            if (v !== notes) onNotesChange(v);
          }}
        />
      </div>
    </section>
  );
};

function ValidationBanner({
  severity,
  icon,
  children,
}: {
  severity: "error" | "warning" | "info";
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls =
    severity === "error"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : severity === "warning"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200"
      : "border-primary/30 bg-primary/5 text-foreground";
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}
      role={severity === "error" ? "alert" : "status"}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ComponentList({
  title,
  help,
  values,
  options,
  disabled,
  onChange,
  highlightMissing,
  gatedBy,
}: {
  title: string;
  help: string;
  values: string[];
  options: string[];
  disabled?: boolean;
  onChange: (v: string[]) => void;
  highlightMissing?: string[];
  /** If provided, options not in this list are disabled (used for Assessed). */
  gatedBy?: string[];
}) {
  const missing = highlightMissing
    ? highlightMissing.filter((c) => !values.includes(c))
    : [];
  const gateSet = gatedBy ? new Set(gatedBy) : null;
  return (
    <div className="space-y-1.5">
      <div>
        <div className="text-xs font-medium text-foreground">{title}</div>
        <div className="text-[11px] text-muted-foreground">{help}</div>
      </div>
      <div className="rounded border border-border bg-background p-2 space-y-1 max-h-56 overflow-auto">
        {options.map((opt) => {
          const checked = values.includes(opt);
          const gateBlocked = gateSet ? !gateSet.has(opt) : false;
          const isDisabled = disabled || gateBlocked;
          return (
            <label
              key={opt}
              className={`flex items-start gap-2 text-xs select-none ${
                isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
              title={gateBlocked ? "Mark as Present first" : undefined}
            >
              <Checkbox
                checked={checked}
                disabled={isDisabled}
                onCheckedChange={(c) =>
                  onChange(c ? [...values, opt] : values.filter((v) => v !== opt))
                }
                className="mt-0.5"
              />
              <span className={checked ? "text-foreground" : "text-muted-foreground"}>
                {opt}
              </span>
            </label>
          );
        })}
      </div>
      {missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
          <span>Present but not assessed:</span>
          {missing.map((m) => (
            <Badge key={m} variant="outline" className="text-[10px]">
              {m}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
